import React, { useState, useEffect } from 'react';
import { ShoppingBag, Plus, Minus, Check, MessageCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent } from './ui/dialog';
import { useCart } from '../context/CartContext';

const ProductModal = ({ product, isOpen, onClose, settings }) => {
  const [quantity, setQuantity] = useState(1);
  const [customNotes, setCustomNotes] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    if (product) {
      setQuantity(product.min_quantity || 1);
      setCustomNotes('');
      setCurrentImageIndex(0);
    }
  }, [product]);

  if (!product) return null;

  // Combine main image with additional images
  const allImages = [
    product.image_url || 'https://via.placeholder.com/400x400?text=Produto',
    ...(product.images || [])
  ].filter(Boolean);

  const getPriceForQuantity = (qty) => {
    if (!product.prices || product.prices.length === 0) return null;
    
    const priceRange = product.prices.find(p => {
      if (p.max_quantity === null) {
        return qty >= p.min_quantity;
      }
      return qty >= p.min_quantity && qty <= p.max_quantity;
    });
    
    return priceRange?.price || null;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const handleAddToCart = () => {
    addItem(product, quantity, customNotes);
    onClose();
  };

  const openWhatsApp = () => {
    const whatsapp = settings?.whatsapp || '5555984551621';
    const productCode = product.code || product.id?.slice(0, 8);
    const message = `Olá! Gostaria de um orçamento para:\n\nProduto: ${product.name}\nCódigo: ${productCode}\nQuantidade: ${quantity} unidades\n${customNotes ? `Observações: ${customNotes}` : ''}`;
    window.open(`https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const currentPrice = getPriceForQuantity(quantity);
  const totalPrice = currentPrice ? currentPrice * quantity : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        <div className="grid md:grid-cols-2 gap-0">
          {/* Product Images - Gallery */}
          <div className="bg-slate-100 relative p-4">
            {/* Main Image - Square with padding */}
            <div className="aspect-square overflow-hidden rounded-xl bg-white">
              <img 
                src={allImages[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Navigation Arrows */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-md"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-700" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-md"
                >
                  <ChevronRight className="w-5 h-5 text-slate-700" />
                </button>
              </>
            )}

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex 
                        ? 'border-[#1B93A4] ring-2 ring-[#1B93A4]/20' 
                        : 'border-transparent hover:border-slate-300'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Image Counter */}
            {allImages.length > 1 && (
              <div className="absolute top-6 left-6 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                {currentImageIndex + 1} / {allImages.length}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="p-6 lg:p-8">
            {/* Code */}
            <p className="font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
              Código: {product.code || product.id?.slice(0, 8)}
            </p>

            {/* Title */}
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-3">
              {product.name}
            </h2>

            {/* Description */}
            {product.description && (
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                {product.description}
              </p>
            )}

            {/* Customizations */}
            {product.customizations && product.customizations.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Opções de Personalização:</h4>
                <div className="space-y-1">
                  {product.customizations.map((custom, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-[#1B93A4]" />
                      <span>{custom}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Price Table */}
            {product.prices && product.prices.length > 0 && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Tabela de Preços:</h4>
                <div className="space-y-2">
                  {product.prices.map((p, idx) => (
                    <div 
                      key={idx} 
                      className={`flex justify-between text-sm px-3 py-2 rounded-lg transition-colors ${
                        currentPrice === p.price 
                          ? 'bg-[#1B93A4] text-white font-medium' 
                          : 'text-slate-600'
                      }`}
                    >
                      <span>{p.min_quantity}{p.max_quantity ? `-${p.max_quantity}` : '+'} unidades</span>
                      <span>{formatPrice(p.price)}/un</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Quantidade <span className="font-normal text-slate-500">(mínimo: {product.min_quantity})</span>
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(product.min_quantity, (parseInt(quantity) || product.min_quantity) - 1))}
                  className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center hover:bg-slate-200 transition-colors"
                >
                  <Minus className="w-4 h-4 text-slate-600" />
                </button>
                <Input
                  type="number"
                  min={product.min_quantity}
                  value={quantity === '' ? '' : quantity}
                  onChange={(e) => setQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (isNaN(val) || val < product.min_quantity) {
                      setQuantity(product.min_quantity);
                    }
                  }}
                  className="w-24 text-center font-semibold"
                />
                <button
                  onClick={() => setQuantity((parseInt(quantity) || product.min_quantity) + 1)}
                  className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center hover:bg-slate-200 transition-colors"
                >
                  <Plus className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Observações <span className="font-normal text-slate-500">(cores, arte, etc.)</span>
              </label>
              <Input
                placeholder="Ex: Cor azul, com logo da empresa..."
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Total Price */}
            {totalPrice && (
              <div className="bg-[#1B93A4]/10 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700 font-medium">Total estimado:</span>
                  <span className="text-2xl font-bold text-[#1B93A4]">{formatPrice(totalPrice)}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleAddToCart}
                className="w-full bg-[#1B93A4] hover:bg-[#157A8C] text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
                data-testid="add-to-cart-modal"
              >
                <ShoppingBag className="w-5 h-5" />
                Adicionar ao Orçamento
              </Button>
              <Button
                onClick={openWhatsApp}
                variant="outline"
                className="w-full border-green-500 text-green-600 hover:bg-green-50 py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Orçar via WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
